import type { AdminRole } from '#shared/adminPermissions';
import type { AdminUserRecord } from '#shared/types';
import {
  dummyCreateAdminUser,
  dummyDeleteAdminUser,
  dummyDisableAdminUser,
  dummyEnableAdminUser,
  dummyFetchAdminUsers,
  dummyResetAdminUserPassword,
  dummyUpdateAdminUserRole,
} from '#src/api/dummyAdminStore';
import { loadRuntimeConfig } from '#src/config';
import { isLocalDemoMode } from '#src/utils/localDemo';

const getApiUrl = async (): Promise<string> => {
  const config = await loadRuntimeConfig();
  return config.apiUrl;
};

const authHeaders = (token: string) => ({
  Authorization: `Bearer ${token}`,
  'Content-Type': 'application/json',
});

const parseError = async (response: Response): Promise<string> => {
  const data = await response.json().catch(() => ({}));
  return typeof data.error === 'string' ? data.error : 'Request failed';
};

export const fetchAdminUsers = async (
  token: string,
): Promise<AdminUserRecord[]> => {
  if (isLocalDemoMode()) {
    return dummyFetchAdminUsers();
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/users`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  const data = (await response.json()) as { users: AdminUserRecord[] };
  return data.users;
};

export const createAdminUser = async (
  token: string,
  input: {
    email: string;
    role: AdminRole;
  },
): Promise<AdminUserRecord> => {
  if (isLocalDemoMode()) {
    return dummyCreateAdminUser(input);
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(`${apiUrl}/admin/users`, {
    method: 'POST',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  });
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
  const data = (await response.json()) as { user: AdminUserRecord };
  return data.user;
};

export const updateAdminUserRole = async (
  token: string,
  username: string,
  role: AdminRole,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyUpdateAdminUserRole(username, role);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/users/${encodeURIComponent(username)}`,
    {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify({ role }),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
};

export const enableAdminUser = async (
  token: string,
  username: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyEnableAdminUser(username);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/users/${encodeURIComponent(username)}/enable`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
};

export const disableAdminUser = async (
  token: string,
  username: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyDisableAdminUser(username);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/users/${encodeURIComponent(username)}/disable`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
};

export const resetAdminUserPassword = async (
  token: string,
  username: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyResetAdminUserPassword(username);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/users/${encodeURIComponent(username)}/reset-password`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
};

export const deleteAdminUser = async (
  token: string,
  username: string,
): Promise<void> => {
  if (isLocalDemoMode()) {
    await dummyDeleteAdminUser(username);
    return;
  }

  const apiUrl = await getApiUrl();
  const response = await fetch(
    `${apiUrl}/admin/users/${encodeURIComponent(username)}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );
  if (!response.ok) {
    throw new Error(await parseError(response));
  }
};
